/**
 * CountryPicker — Searchable modal for selecting country + dial code.
 * Designed like a WhatsApp-style country picker: flag + name + dial code.
 */

import React, { useState, useMemo, useCallback, useId } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  Modal,
  StyleSheet,
  SafeAreaView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, ThemeColors } from '../utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../utils/design-tokens';
import { COUNTRIES, POPULAR_COUNTRY_CODES, type Country } from '../utils/countries';
import { useBackHandler } from '../navigation/useBackHandler';

interface CountryPickerProps {
  selectedCountry: Country;
  onSelect: (country: Country) => void;
}

export default function CountryPicker({ selectedCountry, onSelect }: CountryPickerProps) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const modalId = useId();
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState('');

  const handleClose = useCallback(() => {
    setVisible(false);
    setSearch('');
  }, []);

  const popularCountries = useMemo(
    () => POPULAR_COUNTRY_CODES.map((iso) => COUNTRIES.find((c) => c.iso2 === iso)!).filter(Boolean),
    [],
  );

  const allNonPopular = useMemo(
    () => COUNTRIES.filter((c) => !POPULAR_COUNTRY_CODES.includes(c.iso2)),
    [],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.iso2.toLowerCase() === q,
    );
  }, [search]);

  const isSearching = !!search.trim();

  const handleSelect = useCallback(
    (country: Country) => {
      onSelect(country);
      handleClose();
    },
    [onSelect, handleClose],
  );

  useBackHandler({
    id: `country-picker-${modalId}`,
    label: 'Country picker',
    priority: 950,
    enabled: visible,
    onBack: () => {
      handleClose();
      return true;
    },
  });

  const renderItem = useCallback(
    ({ item }: { item: Country }) => (
      <TouchableOpacity style={s.row} onPress={() => handleSelect(item)}>
        <Text style={s.flag}>{item.flag}</Text>
        <Text style={s.name} numberOfLines={1}>
          {item.name}
        </Text>
        <Text style={s.code}>+{item.dialCode}</Text>
      </TouchableOpacity>
    ),
    [handleSelect],
  );

  return (
    <>
      {/* Trigger button */}
      <TouchableOpacity style={s.trigger} onPress={() => setVisible(true)} accessibilityLabel="Select country code">
        <Text style={s.triggerFlag}>{selectedCountry.flag}</Text>
        <Text style={s.triggerCode} numberOfLines={1}>+{selectedCountry.dialCode}</Text>
        <Ionicons name="chevron-down" size={14} color={colors.textSecondary} />
      </TouchableOpacity>

      {/* Modal */}
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle={Platform.OS === 'web' ? 'fullScreen' : 'pageSheet'}
        onRequestClose={handleClose}
      >
        <SafeAreaView style={s.modal}>
          {/* Header */}
          <View style={s.header}>
            <Text style={s.headerTitle}>Select Country</Text>
            <TouchableOpacity onPress={handleClose} accessibilityRole="button" accessibilityLabel="Close">
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {/* Search */}
          <View style={s.searchContainer}>
            <Ionicons name="search" size={18} color={colors.textSecondary} style={s.searchIcon} />
            <TextInput
              style={s.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder="Search country or dial code..."
              placeholderTextColor={colors.textSecondary}
              autoFocus={Platform.OS !== 'web'}
              autoCorrect={false}
            />
          </View>

          {isSearching ? (
            <FlatList
              data={filtered}
              keyExtractor={(item) => item.iso2}
              renderItem={renderItem}
              keyboardShouldPersistTaps="handled"
              ListEmptyComponent={
                <Text style={s.empty}>No countries found</Text>
              }
            />
          ) : (
            <FlatList
              data={allNonPopular}
              keyExtractor={(item) => item.iso2}
              renderItem={renderItem}
              keyboardShouldPersistTaps="handled"
              ListHeaderComponent={
                <>
                  <Text style={s.sectionLabel}>Popular</Text>
                  {popularCountries.map((item) => (
                    <TouchableOpacity key={item.iso2} style={s.row} onPress={() => handleSelect(item)}>
                      <Text style={s.flag}>{item.flag}</Text>
                      <Text style={s.name} numberOfLines={1}>{item.name}</Text>
                      <Text style={s.code}>+{item.dialCode}</Text>
                    </TouchableOpacity>
                  ))}
                  <Text style={s.sectionLabel}>All Countries</Text>
                </>
              }
            />
          )}
        </SafeAreaView>
      </Modal>
    </>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.sm + 2,
    paddingVertical: SPACING.md,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 4,
    flexShrink: 0,
    minWidth: 98,
  },
  triggerFlag: {
    fontSize: FONT_SIZE.base,
    minWidth: 20,
    textAlign: 'center',
  },
  triggerCode: {
    fontSize: FONT_SIZE.sm,
    color: colors.text,
    fontWeight: '600',
    flexShrink: 1,
  },
  modal: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.base,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.md,
    marginHorizontal: SPACING.lg,
    marginVertical: SPACING.md,
    paddingHorizontal: SPACING.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchIcon: {
    marginRight: SPACING.sm,
  },
  searchInput: {
    flex: 1,
    paddingVertical: SPACING.md,
    fontSize: FONT_SIZE.base,
    color: colors.text,
  },
  sectionLabel: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
    marginHorizontal: SPACING.lg,
    marginBottom: SPACING.xs,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  flag: {
    fontSize: FONT_SIZE.xl,
    marginRight: SPACING.md,
  },
  name: {
    flex: 1,
    fontSize: FONT_SIZE.base,
    color: colors.text,
  },
  code: {
    fontSize: FONT_SIZE.base,
    color: colors.textSecondary,
    fontWeight: '500',
    marginLeft: SPACING.sm,
  },
  empty: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: SPACING['3xl'],
    fontSize: FONT_SIZE.base,
  },
});
